import { TApiResponse } from "@/types/api";
import { handleAsync } from "@/utils";
import { downloadBlobWithFilename } from "@/utils/downloadBlob";
import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";
import {
  TAdminCompany,
  TAdminFile,
  TAdminPermissionDetail,
  TAdminRole,
  TAdminSection,
  TAdminUser,
  TChatCategory,
  TChatMessage,
  TChatStatsOverview,
  TChatThreadsQueryParams,
  TCreateChatCategoryRequest,
  TCreatePermissionRequest,
  TCreateRoleRequest,
  TCreateSectionRequest,
  TOnboardingAnswersQueryParams,
  TOnboardingStatsOverview,
  TReviewer,
  TPaginatedRoleUsers,
  TUpdateChatCategoryRequest,
  TUpdatePermissionRequest,
  TUpdateRoleRequest,
  TUpdateSectionRequest,
  TAnalyticsData,
  TAnalyticsQueryParams,
  TAdminAuditEvent,
  TAuditSearchQueryParams,
  TAuditSearchResult,
  TAuditStats,
  TAuditStatsQueryParams,
  TCompaniesQueryParams,
  TCompanyCountStats,
  TCompanySection,
  TCompanyStatsOverview,
  TCompanySummary,
  TCostTokenUsage,
  TCostTokenUsageQueryParams,
  TCreateOnboardingQuestionRequest,
  TCreateUserRequest,
  TDownloadAnalyticsReportParams,
  TEffectivePermissions,
  TEngagement,
  TKeyMetrics,
  TLeaderboard,
  TLeaderboardQueryParams,
  TLoginAttemptsQueryParams,
  TFailedLoginAttemptsQueryParams,
  TLoginStats,
  TLoginStatsQueryParams,
  TPaginatedCompanies,
  TPaginatedLoginAttempts,
  TPaginatedWhatsAppGroups,
  TWhatsAppGroupsQueryParams,
  TOnboardingQuestion,
  TOnboardingQuestionsQueryParams,
  TOnboardingQuestionsResponse,
  TPromoteUserRequest,
  TPromoteUserResponse,
  TReorderQuestionsRequest,
  TRoleResponse,
  TGdprRequest,
  TGdprRequestsQueryParams,
  TPaginatedGdprRequests,
  TSystemConfig,
  TUpdateGdprRequest,
  TUpdateOnboardingQuestionRequest,
  TUpdateSystemConfigRequest,
  TUpdateUserRequest,
  TUserAuditTrail,
  TUserAuditTrailQueryParams,
  TUsersListResponse,
  TUsersQueryParams,
  TUserSummary,
  TUserTokens,
  TWhatsAppGroupDetail,
  TWhatsAppTemplate,
  TUpdateWhatsAppTemplateRequest,
  TWhatsAppAnalyticsSummary,
  TWhatsAppAnalyticsQueryParams,
  TAiUsageStats,
  TAiModelStats,
  TAiUsageLogsQueryParams,
  TAiContext,
  TFilesQueryParams,
  TFileStatsOverview,
  TPaginatedFiles,
  TPaginatedAiUsageLogs,
  TPaginatedChatThreads,
  TPaginatedOnboardingAnswers,
  TInvestorPlatformInvestor,
  TPaginatedInvestorPlatformInvestors,
  TInvestorPlatformInvestorsQueryParams,
  TCreateInvestorPlatformInvestorRequest,
  TUpdateInvestorPlatformInvestorRequest,
  TConnectRequest,
  TPaginatedConnectRequests,
  TConnectRequestsQueryParams,
  TUpdateConnectRequestStatusRequest,
} from "./admin.types";

import { withAuthHooks } from "../auth/auth.ky.hooks";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class AdminService {
  private rootEndpoint = "admin" as const;

  // Analytics
  getAnalytics(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAnalyticsData>>(() =>
      apiService.get<TApiResponse<TAnalyticsData>>(`${this.rootEndpoint}/analytics`, options)
    );
  }

  getAnalyticsOverview(params?: TAnalyticsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append("date_from", params.date_from);
    if (params?.date_to) queryParams.append("date_to", params.date_to);
    if (params?.role_type) queryParams.append("role_type", params.role_type);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/analytics/overview?${queryString}`
      : `${this.rootEndpoint}/analytics/overview`;

    return handleAsync<
      TApiServiceError,
      TApiResponse<{
        key_metrics: TKeyMetrics;
        user_summary: TUserSummary;
        engagement: TEngagement;
      }>
    >(() =>
      apiService.get<
        TApiResponse<{
          key_metrics: TKeyMetrics;
          user_summary: TUserSummary;
          engagement: TEngagement;
        }>
      >(endpoint, options)
    );
  }

  getAnalyticsKeyMetrics(params?: TAnalyticsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.duration) {
      queryParams.append("duration", params.duration);
      if (params.duration === "custom") {
        if (params.date_from) queryParams.append("date_from", params.date_from);
        if (params.date_to) queryParams.append("date_to", params.date_to);
      }
    } else {
      if (params?.date_from) queryParams.append("date_from", params.date_from);
      if (params?.date_to) queryParams.append("date_to", params.date_to);
    }
    if (params?.role_type) queryParams.append("role_type", params.role_type);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/analytics/key-metrics?${queryString}`
      : `${this.rootEndpoint}/analytics/key-metrics`;

    return handleAsync<TApiServiceError, TApiResponse<TKeyMetrics>>(() =>
      apiService.get<TApiResponse<TKeyMetrics>>(endpoint, options)
    );
  }

  getAnalyticsUserSummary(params?: TAnalyticsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.duration) {
      queryParams.append("duration", params.duration);
      if (params.duration === "custom") {
        if (params.date_from) queryParams.append("date_from", params.date_from);
        if (params.date_to) queryParams.append("date_to", params.date_to);
      }
    } else {
      if (params?.date_from) queryParams.append("date_from", params.date_from);
      if (params?.date_to) queryParams.append("date_to", params.date_to);
    }
    if (params?.role_type) queryParams.append("role_type", params.role_type);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/analytics/user-summary?${queryString}`
      : `${this.rootEndpoint}/analytics/user-summary`;

    return handleAsync<TApiServiceError, TApiResponse<TUserSummary>>(() =>
      apiService.get<TApiResponse<TUserSummary>>(endpoint, options)
    );
  }

  getAnalyticsEngagement(params?: TAnalyticsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.duration) {
      queryParams.append("duration", params.duration);
      if (params.duration === "custom") {
        if (params.date_from) queryParams.append("date_from", params.date_from);
        if (params.date_to) queryParams.append("date_to", params.date_to);
      }
    } else {
      if (params?.date_from) queryParams.append("date_from", params.date_from);
      if (params?.date_to) queryParams.append("date_to", params.date_to);
    }
    if (params?.role_type) queryParams.append("role_type", params.role_type);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/analytics/engagement?${queryString}`
      : `${this.rootEndpoint}/analytics/engagement`;

    return handleAsync<TApiServiceError, TApiResponse<TEngagement>>(() =>
      apiService.get<TApiResponse<TEngagement>>(endpoint, options)
    );
  }

  getAnalyticsCostTokenUsage(params?: TCostTokenUsageQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.duration) {
      queryParams.append("duration", params.duration);
      if (params.duration === "custom") {
        if (params.date_from) queryParams.append("date_from", params.date_from);
        if (params.date_to) queryParams.append("date_to", params.date_to);
      }
    } else {
      if (params?.date_from) queryParams.append("date_from", params.date_from);
      if (params?.date_to) queryParams.append("date_to", params.date_to);
    }
    if (params?.model_name) queryParams.append("model_name", params.model_name);
    if (params?.role_type) queryParams.append("role_type", params.role_type);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/analytics/cost-token-usage?${queryString}`
      : `${this.rootEndpoint}/analytics/cost-token-usage`;

    return handleAsync<TApiServiceError, TApiResponse<TCostTokenUsage>>(() =>
      apiService.get<TApiResponse<TCostTokenUsage>>(endpoint, options)
    );
  }

  getAnalyticsCompanySummary(params?: TAnalyticsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.duration) {
      queryParams.append("duration", params.duration);
      if (params.duration === "custom") {
        if (params.date_from) queryParams.append("date_from", params.date_from);
        if (params.date_to) queryParams.append("date_to", params.date_to);
      }
    } else {
      if (params?.date_from) queryParams.append("date_from", params.date_from);
      if (params?.date_to) queryParams.append("date_to", params.date_to);
    }
    if (params?.role_type) queryParams.append("role_type", params.role_type);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/analytics/company-summary?${queryString}`
      : `${this.rootEndpoint}/analytics/company-summary`;

    return handleAsync<TApiServiceError, TApiResponse<TCompanySummary>>(() =>
      apiService.get<TApiResponse<TCompanySummary>>(endpoint, options)
    );
  }

  getAnalyticsLeaderboard(params?: TLeaderboardQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.role_type) queryParams.append("role_type", params.role_type);
    if (params?.duration) {
      queryParams.append("duration", params.duration);
      if (params.duration === "custom") {
        if (params.date_from) queryParams.append("date_from", params.date_from);
        if (params.date_to) queryParams.append("date_to", params.date_to);
      }
    } else {
      if (params?.date_from) queryParams.append("date_from", params.date_from);
      if (params?.date_to) queryParams.append("date_to", params.date_to);
    }
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/analytics/leaderboard?${queryString}`
      : `${this.rootEndpoint}/analytics/leaderboard`;

    return handleAsync<TApiServiceError, TApiResponse<TLeaderboard>>(() =>
      apiService.get<TApiResponse<TLeaderboard>>(endpoint, options)
    );
  }

  downloadAnalyticsReport(params: TDownloadAnalyticsReportParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    queryParams.append("duration", params.duration);

    if (params.duration === "custom") {
      if (params.date_from) queryParams.append("date_from", params.date_from);
      if (params.date_to) queryParams.append("date_to", params.date_to);
    }
    if (params.role_type) queryParams.append("role_type", params.role_type);
    if (params.model_name) queryParams.append("model_name", params.model_name);
    if (params.sort_by) queryParams.append("sort_by", params.sort_by);
    if (typeof params.leaderboard_limit === "number") {
      queryParams.append("leaderboard_limit", params.leaderboard_limit.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/analytics/report?${queryString}`
      : `${this.rootEndpoint}/analytics/report`;

    return handleAsync<
      TApiServiceError,
      {
        blob: Blob;
        filename: string | null;
      }
    >(async () => {
      const response = await apiService.getRaw(endpoint, options);
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") ?? response.headers.get("Content-Disposition");
      let filename: string | null = null;

      if (disposition) {
        const filenameRegex = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i;
        const filenameMatch = filenameRegex.exec(disposition);
        if (filenameMatch?.[1]) {
          try {
            filename = decodeURIComponent(filenameMatch[1]);
          } catch {
            filename = filenameMatch[1];
          }
        }
      }

      return {
        blob,
        filename,
      };
    });
  }

  // User Management
  getUsers(params?: TUsersQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", String(params.page));
    if (params?.page_size) queryParams.append("per_page", String(params.page_size));
    if (params?.search) queryParams.append("search", params.search);
    // The users endpoint expects filters as `filters=field:value` and sort as `sort=field:dir`.
    if (params?.status_id !== undefined)
      queryParams.append("filters", `status_id:${params.status_id}`);
    if (params?.sort_by)
      queryParams.append("sort", `${params.sort_by}:${params.sort_order ?? "desc"}`);

    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.rootEndpoint}/users?${queryString}` : `${this.rootEndpoint}/users`;

    return handleAsync<TApiServiceError, TApiResponse<TUsersListResponse>>(() =>
      apiService.get<TApiResponse<TUsersListResponse>>(endpoint, options)
    );
  }

  getUser(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminUser>>(() =>
      apiService.get<TApiResponse<TAdminUser>>(`${this.rootEndpoint}/users/${userId}`, options)
    );
  }

  updateUser(userId: string, payload: TUpdateUserRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminUser>>(() =>
      apiService.patch<TApiResponse<TAdminUser>>(`${this.rootEndpoint}/users/${userId}`, payload, options)
    );
  }

  deleteUser(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ message: string }>>(() =>
      apiService.delete<TApiResponse<{ message: string }>>(`${this.rootEndpoint}/users/${userId}`, undefined, options)
    );
  }

  createUser(payload: TCreateUserRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminUser>>(() =>
      apiService.post<TApiResponse<TAdminUser>>(`${this.rootEndpoint}/users`, payload, options)
    );
  }

  activateUser(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminUser>>(() =>
      apiService.post<TApiResponse<TAdminUser>>(`${this.rootEndpoint}/users/${userId}/activate`, undefined, options)
    );
  }

  deactivateUser(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminUser>>(() =>
      apiService.post<TApiResponse<TAdminUser>>(`${this.rootEndpoint}/users/${userId}/deactivate`, undefined, options)
    );
  }

  unlockUser(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminUser>>(() =>
      apiService.post<TApiResponse<TAdminUser>>(`${this.rootEndpoint}/users/${userId}/unlock`, undefined, options)
    );
  }

  verifyUserEmail(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminUser>>(() =>
      apiService.post<TApiResponse<TAdminUser>>(`${this.rootEndpoint}/users/${userId}/verify-email`, undefined, options)
    );
  }

  verifyUserPhone(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminUser>>(() =>
      apiService.post<TApiResponse<TAdminUser>>(`${this.rootEndpoint}/users/${userId}/verify-phone`, undefined, options)
    );
  }

  getUserPermissions(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TEffectivePermissions>>(() =>
      apiService.get<TApiResponse<TEffectivePermissions>>(`${this.rootEndpoint}/users/${userId}/permissions`, options)
    );
  }

  // Tokens / Sessions
  getUserTokens(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TUserTokens>>(() =>
      apiService.get<TApiResponse<TUserTokens>>(`${this.rootEndpoint}/tokens/users/${userId}/tokens`, options)
    );
  }

  revokeAuthToken(tokenId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<null>>(() =>
      apiService.post<TApiResponse<null>>(
        `${this.rootEndpoint}/tokens/auth-tokens/${tokenId}/revoke`,
        undefined,
        options
      )
    );
  }

  revokeAllUserTokens(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<null>>(() =>
      apiService.post<TApiResponse<null>>(
        `${this.rootEndpoint}/tokens/users/${userId}/tokens/revoke-all`,
        undefined,
        options
      )
    );
  }

  // Login Attempts
  getUserLoginAttempts(
    userId: string,
    params?: { limit?: number; offset?: number },
    options?: TApiServiceOptions
  ) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/login-attempts/users/${userId}/attempts?${queryString}`
      : `${this.rootEndpoint}/login-attempts/users/${userId}/attempts`;

    return handleAsync<TApiServiceError, TApiResponse<TPaginatedLoginAttempts>>(() =>
      apiService.get<TApiResponse<TPaginatedLoginAttempts>>(endpoint, options)
    );
  }

  // Audit Trail
  getUserAuditTrail(userId: string, params?: TUserAuditTrailQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", String(params.page));
    if (params?.page_size) queryParams.append("page_size", String(params.page_size));

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/audit/users/${userId}?${queryString}`
      : `${this.rootEndpoint}/audit/users/${userId}`;

    return handleAsync<TApiServiceError, TApiResponse<TUserAuditTrail>>(() =>
      apiService.get<TApiResponse<TUserAuditTrail>>(endpoint, options)
    );
  }

  // Companies
  getCompanies(params?: TCompaniesQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params?.role) queryParams.append("role", params.role);
    if (params?.onboarding_completed !== undefined)
      queryParams.append("onboarding_completed", String(params.onboarding_completed));

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/companies/?${queryString}`
      : `${this.rootEndpoint}/companies/`;

    return handleAsync<TApiServiceError, TApiResponse<TPaginatedCompanies>>(() =>
      apiService.get<TApiResponse<TPaginatedCompanies>>(endpoint, options)
    );
  }

  getCompany(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminCompany>>(() =>
      apiService.get<TApiResponse<TAdminCompany>>(`${this.rootEndpoint}/companies/${companyId}`, options)
    );
  }

  getCompanySections(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanySection[]>>(() =>
      apiService.get<TApiResponse<TCompanySection[]>>(`${this.rootEndpoint}/companies/${companyId}/sections`, options)
    );
  }

  getCompanyStatsOverview(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyStatsOverview>>(() =>
      apiService.get<TApiResponse<TCompanyStatsOverview>>(`${this.rootEndpoint}/companies/stats/overview`, options)
    );
  }

  getCompanyStatsByType(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyCountStats>>(() =>
      apiService.get<TApiResponse<TCompanyCountStats>>(`${this.rootEndpoint}/companies/stats/by-type`, options)
    );
  }

  getCompanyStatsByStatus(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyCountStats>>(() =>
      apiService.get<TApiResponse<TCompanyCountStats>>(`${this.rootEndpoint}/companies/stats/by-status`, options)
    );
  }

  // Admin Roles
  getRoles(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TRoleResponse[]>>(() =>
      apiService.get<TApiResponse<TRoleResponse[]>>(`${this.rootEndpoint}/roles`, options)
    );
  }

  // Admin Promotion
  promoteUser(email: string, roleName: string, options?: TApiServiceOptions) {
    const payload: TPromoteUserRequest = {
      email,
      role_name: roleName,
    };
    return handleAsync<TApiServiceError, TApiResponse<TPromoteUserResponse>>(() =>
      apiService.post<TApiResponse<TPromoteUserResponse>>(`${this.rootEndpoint}/users/promote`, payload, options)
    );
  }

  assignRole(userId: string, roleName: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TPromoteUserResponse>>(() =>
      apiService.post<TApiResponse<TPromoteUserResponse>>(
        `${this.rootEndpoint}/users/${userId}/roles`,
        { role_name: roleName },
        options
      )
    );
  }

  revokeRole(userId: string, roleName: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TPromoteUserResponse>>(() =>
      apiService.delete<TApiResponse<TPromoteUserResponse>>(
        `${this.rootEndpoint}/users/${userId}/roles/${roleName}`,
        undefined,
        options
      )
    );
  }

  // System Configuration
  getSystemConfig(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TSystemConfig>>(() =>
      apiService.get<TApiResponse<TSystemConfig>>(`${this.rootEndpoint}/system/config`, options)
    );
  }

  updateSystemConfig(payload: TUpdateSystemConfigRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TSystemConfig>>(() =>
      apiService.patch<TApiResponse<TSystemConfig>>(`${this.rootEndpoint}/system/config`, payload, options)
    );
  }

  // GDPR request queue (admin/gdpr/*)
  getGdprRequests(params?: TGdprRequestsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.request_type) queryParams.append("request_type", params.request_type);
    if (params?.user_id) queryParams.append("user_id", params.user_id);
    if (params?.date_from) queryParams.append("date_from", params.date_from);
    if (params?.date_to) queryParams.append("date_to", params.date_to);
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/gdpr/requests?${queryString}`
      : `${this.rootEndpoint}/gdpr/requests`;

    return handleAsync<TApiServiceError, TApiResponse<TPaginatedGdprRequests>>(() =>
      apiService.get<TApiResponse<TPaginatedGdprRequests>>(endpoint, options)
    );
  }

  getGdprRequest(requestId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TGdprRequest>>(() =>
      apiService.get<TApiResponse<TGdprRequest>>(`${this.rootEndpoint}/gdpr/requests/${requestId}`, options)
    );
  }

  updateGdprRequest(requestId: string, payload: TUpdateGdprRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TGdprRequest>>(() =>
      apiService.patch<TApiResponse<TGdprRequest>>(
        `${this.rootEndpoint}/gdpr/requests/${requestId}`,
        payload,
        options
      )
    );
  }

  // Audit Logs (admin/audit/*)
  searchAuditLogs(params?: TAuditSearchQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.user_id) queryParams.append("user_id", params.user_id);
    if (params?.event_types) {
      params.event_types.forEach((t) => queryParams.append("event_types", t));
    }
    if (params?.target_entity) queryParams.append("target_entity", params.target_entity);
    if (params?.target_id) queryParams.append("target_id", params.target_id);
    if (params?.date_from) queryParams.append("date_from", params.date_from);
    if (params?.date_to) queryParams.append("date_to", params.date_to);
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/audit/search?${queryString}`
      : `${this.rootEndpoint}/audit/search`;

    return handleAsync<TApiServiceError, TApiResponse<TAuditSearchResult>>(() =>
      apiService.get<TApiResponse<TAuditSearchResult>>(endpoint, options)
    );
  }

  getRecentAuditEvents(params?: { limit?: number }, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/audit/recent?${queryString}`
      : `${this.rootEndpoint}/audit/recent`;

    return handleAsync<TApiServiceError, TApiResponse<TAdminAuditEvent[]>>(() =>
      apiService.get<TApiResponse<TAdminAuditEvent[]>>(endpoint, options)
    );
  }

  getAuditStats(params?: TAuditStatsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append("date_from", params.date_from);
    if (params?.date_to) queryParams.append("date_to", params.date_to);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/audit/stats?${queryString}`
      : `${this.rootEndpoint}/audit/stats`;

    return handleAsync<TApiServiceError, TApiResponse<TAuditStats>>(() =>
      apiService.get<TApiResponse<TAuditStats>>(endpoint, options)
    );
  }

  // Login Attempts (admin/login-attempts/*)
  getLoginAttempts(params?: TLoginAttemptsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.user_id) queryParams.append("user_id", params.user_id);
    if (params?.successful !== undefined) queryParams.append("successful", String(params.successful));
    if (params?.ip_address) queryParams.append("ip_address", params.ip_address);
    if (params?.date_from) queryParams.append("date_from", params.date_from);
    if (params?.date_to) queryParams.append("date_to", params.date_to);
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/login-attempts?${queryString}`
      : `${this.rootEndpoint}/login-attempts`;

    return handleAsync<TApiServiceError, TApiResponse<TPaginatedLoginAttempts>>(() =>
      apiService.get<TApiResponse<TPaginatedLoginAttempts>>(endpoint, options)
    );
  }

  getFailedLoginAttempts(
    params?: TFailedLoginAttemptsQueryParams,
    options?: TApiServiceOptions
  ) {
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append("date_from", params.date_from);
    if (params?.date_to) queryParams.append("date_to", params.date_to);
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/login-attempts/failed/list?${queryString}`
      : `${this.rootEndpoint}/login-attempts/failed/list`;

    return handleAsync<TApiServiceError, TApiResponse<TPaginatedLoginAttempts>>(() =>
      apiService.get<TApiResponse<TPaginatedLoginAttempts>>(endpoint, options)
    );
  }

  getLoginStats(params?: TLoginStatsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append("date_from", params.date_from);
    if (params?.date_to) queryParams.append("date_to", params.date_to);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/login-attempts/stats/summary?${queryString}`
      : `${this.rootEndpoint}/login-attempts/stats/summary`;

    return handleAsync<TApiServiceError, TApiResponse<TLoginStats>>(() =>
      apiService.get<TApiResponse<TLoginStats>>(endpoint, options)
    );
  }

  // Onboarding Questions
  getOnboardingQuestions(params?: TOnboardingQuestionsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.role_type) queryParams.append("role_type", params.role_type);
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));
    if (params?.is_active !== undefined) queryParams.append("is_active", String(params.is_active));

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/onboarding/questions?${queryString}`
      : `${this.rootEndpoint}/onboarding/questions`;

    return handleAsync<TApiServiceError, TApiResponse<TOnboardingQuestionsResponse>>(() =>
      apiService.get<TApiResponse<TOnboardingQuestionsResponse>>(endpoint, options)
    );
  }

  createOnboardingQuestion(payload: TCreateOnboardingQuestionRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ data: TOnboardingQuestion }>>(() =>
      apiService.post<TApiResponse<{ data: TOnboardingQuestion }>>(
        `${this.rootEndpoint}/onboarding/questions`,
        payload,
        options
      )
    );
  }

  updateOnboardingQuestion(
    questionId: string,
    payload: TUpdateOnboardingQuestionRequest,
    options?: TApiServiceOptions
  ) {
    return handleAsync<TApiServiceError, TApiResponse<{ data: TOnboardingQuestion }>>(() =>
      apiService.put<TApiResponse<{ data: TOnboardingQuestion }>>(
        `${this.rootEndpoint}/onboarding/questions/${questionId}`,
        payload,
        options
      )
    );
  }

  deleteOnboardingQuestion(questionId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, void>(() =>
      apiService.delete<void>(`${this.rootEndpoint}/onboarding/questions/${questionId}`, undefined, options)
    );
  }

  reorderOnboardingQuestions(payload: TReorderQuestionsRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ message: string }>>(() =>
      apiService.post<TApiResponse<{ message: string }>>(
        `${this.rootEndpoint}/onboarding/questions/reorder`,
        payload,
        options
      )
    );
  }

  // WhatsApp Introduction Groups
  getWhatsAppGroups(params?: TWhatsAppGroupsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/whatsapp-groups?${queryString}`
      : `${this.rootEndpoint}/whatsapp-groups`;
    return handleAsync<TApiServiceError, TApiResponse<TPaginatedWhatsAppGroups>>(() =>
      apiService.get<TApiResponse<TPaginatedWhatsAppGroups>>(endpoint, options)
    );
  }

  getWhatsAppGroup(groupId: string, params?: { limit?: number; offset?: number }, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/whatsapp-groups/${groupId}?${queryString}`
      : `${this.rootEndpoint}/whatsapp-groups/${groupId}`;
    return handleAsync<TApiServiceError, TApiResponse<TWhatsAppGroupDetail>>(() =>
      apiService.get<TApiResponse<TWhatsAppGroupDetail>>(endpoint, options)
    );
  }

  sendWhatsAppGroupMessage(
    groupId: string,
    payload: { text: string },
    options?: TApiServiceOptions
  ) {
    return handleAsync<TApiServiceError, TApiResponse<{ ok: boolean; wamid?: string }>>(() =>
      apiService.post<TApiResponse<{ ok: boolean; wamid?: string }>>(
        `${this.rootEndpoint}/whatsapp-groups/${groupId}/messages`,
        payload,
        options
      )
    );
  }

  // ── Roles & Permissions (admin/roles/*, admin/permissions/*) ──
  createRole(payload: TCreateRoleRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminRole>>(() =>
      apiService.post<TApiResponse<TAdminRole>>(`${this.rootEndpoint}/roles`, payload, options)
    );
  }

  updateRole(roleId: string, payload: TUpdateRoleRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminRole>>(() =>
      apiService.put<TApiResponse<TAdminRole>>(`${this.rootEndpoint}/roles/${roleId}`, payload, options)
    );
  }

  deleteRole(roleId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<unknown>>(() =>
      apiService.delete<TApiResponse<unknown>>(`${this.rootEndpoint}/roles/${roleId}`, undefined, options)
    );
  }

  getRolePermissions(roleId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminPermissionDetail[]>>(() =>
      apiService.get<TApiResponse<TAdminPermissionDetail[]>>(
        `${this.rootEndpoint}/roles/${roleId}/permissions`,
        options
      )
    );
  }

  assignPermissionsToRole(roleId: string, permissionIds: string[], options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<unknown>>(() =>
      apiService.post<TApiResponse<unknown>>(
        `${this.rootEndpoint}/roles/${roleId}/permissions`,
        { permission_ids: permissionIds },
        options
      )
    );
  }

  revokePermissionFromRole(roleId: string, permissionId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<unknown>>(() =>
      apiService.delete<TApiResponse<unknown>>(
        `${this.rootEndpoint}/roles/${roleId}/permissions/${permissionId}`,
        undefined,
        options
      )
    );
  }

  getRoleUsers(roleId: string, params?: { limit?: number; offset?: number }, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/roles/${roleId}/users?${queryString}`
      : `${this.rootEndpoint}/roles/${roleId}/users`;

    return handleAsync<TApiServiceError, TApiResponse<TPaginatedRoleUsers>>(() =>
      apiService.get<TApiResponse<TPaginatedRoleUsers>>(endpoint, options)
    );
  }

  assignRoleToUser(roleId: string, userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<unknown>>(() =>
      apiService.post<TApiResponse<unknown>>(
        `${this.rootEndpoint}/roles/${roleId}/users/${userId}`,
        undefined,
        options
      )
    );
  }

  revokeRoleFromUser(roleId: string, userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<unknown>>(() =>
      apiService.delete<TApiResponse<unknown>>(
        `${this.rootEndpoint}/roles/${roleId}/users/${userId}`,
        undefined,
        options
      )
    );
  }

  getPermissions(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminPermissionDetail[]>>(() =>
      apiService.get<TApiResponse<TAdminPermissionDetail[]>>(`${this.rootEndpoint}/permissions`, options)
    );
  }

  createPermission(payload: TCreatePermissionRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminPermissionDetail>>(() =>
      apiService.post<TApiResponse<TAdminPermissionDetail>>(`${this.rootEndpoint}/permissions`, payload, options)
    );
  }

  updatePermission(permissionId: string, payload: TUpdatePermissionRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminPermissionDetail>>(() =>
      apiService.put<TApiResponse<TAdminPermissionDetail>>(
        `${this.rootEndpoint}/permissions/${permissionId}`,
        payload,
        options
      )
    );
  }

  deletePermission(permissionId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<unknown>>(() =>
      apiService.delete<TApiResponse<unknown>>(
        `${this.rootEndpoint}/permissions/${permissionId}`,
        undefined,
        options
      )
    );
  }

  // ── Reviewers (admin/reviewers) ──
  getReviewers(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TReviewer[]>>(() =>
      apiService.get<TApiResponse<TReviewer[]>>(`${this.rootEndpoint}/reviewers`, options)
    );
  }

  assignReviewer(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TReviewer>>(() =>
      apiService.post<TApiResponse<TReviewer>>(
        `${this.rootEndpoint}/reviewers`,
        { user_id: userId },
        options
      )
    );
  }

  removeReviewer(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TReviewer>>(() =>
      apiService.delete<TApiResponse<TReviewer>>(
        `${this.rootEndpoint}/reviewers/${userId}`,
        undefined,
        options
      )
    );
  }

  // ── Onboarding answers & files (admin/onboarding/*) ──
  getOnboardingAnswers(params?: TOnboardingAnswersQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.company_id) queryParams.append("company_id", params.company_id);
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/onboarding/answers?${queryString}`
      : `${this.rootEndpoint}/onboarding/answers`;
    return handleAsync<TApiServiceError, TApiResponse<TPaginatedOnboardingAnswers>>(() =>
      apiService.get<TApiResponse<TPaginatedOnboardingAnswers>>(endpoint, options)
    );
  }

  getOnboardingStats(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TOnboardingStatsOverview>>(() =>
      apiService.get<TApiResponse<TOnboardingStatsOverview>>(
        `${this.rootEndpoint}/onboarding/stats/overview`,
        options
      )
    );
  }

  getOnboardingCompanyFiles(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminFile[]>>(() =>
      apiService.get<TApiResponse<TAdminFile[]>>(
        `${this.rootEndpoint}/onboarding/files/${companyId}`,
        options
      )
    );
  }

  downloadOnboardingCompanyFiles(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, { blob: Blob; filename: string | null }>(async () => {
      const response = await apiService.getRaw(
        `${this.rootEndpoint}/onboarding/files/${companyId}/download-all`,
        options
      );
      return downloadBlobWithFilename(response);
    });
  }

  // ── Chat moderation (admin/chat/*) ──
  getChatThreads(params?: TChatThreadsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.company_id) queryParams.append("company_id", params.company_id);
    if (params?.section_id) queryParams.append("section_id", params.section_id);
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/chat/threads?${queryString}`
      : `${this.rootEndpoint}/chat/threads`;
    return handleAsync<TApiServiceError, TApiResponse<TPaginatedChatThreads>>(() =>
      apiService.get<TApiResponse<TPaginatedChatThreads>>(endpoint, options)
    );
  }

  getChatThreadMessages(threadId: string, limit = 50, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatMessage[]>>(() =>
      apiService.get<TApiResponse<TChatMessage[]>>(
        `${this.rootEndpoint}/chat/threads/${threadId}/messages?limit=${limit}`,
        options
      )
    );
  }

  getChatStats(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatStatsOverview>>(() =>
      apiService.get<TApiResponse<TChatStatsOverview>>(`${this.rootEndpoint}/chat/stats/overview`, options)
    );
  }

  deleteChatThread(threadId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ message: string }>>(() =>
      apiService.delete<TApiResponse<{ message: string }>>(
        `${this.rootEndpoint}/chat/threads/${threadId}`,
        undefined,
        options
      )
    );
  }

  deleteChatMessage(threadId: string, messageId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ message: string }>>(() =>
      apiService.delete<TApiResponse<{ message: string }>>(
        `${this.rootEndpoint}/chat/threads/${threadId}/messages/${messageId}`,
        undefined,
        options
      )
    );
  }

  getChatCategories(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatCategory[]>>(() =>
      apiService.get<TApiResponse<TChatCategory[]>>(
        `${this.rootEndpoint}/chat/categories?limit=100`,
        options
      )
    );
  }

  createChatCategory(payload: TCreateChatCategoryRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatCategory>>(() =>
      apiService.post<TApiResponse<TChatCategory>>(`${this.rootEndpoint}/chat/categories`, payload, options)
    );
  }

  updateChatCategory(categoryId: string, payload: TUpdateChatCategoryRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatCategory>>(() =>
      apiService.put<TApiResponse<TChatCategory>>(
        `${this.rootEndpoint}/chat/categories/${categoryId}`,
        payload,
        options
      )
    );
  }

  deleteChatCategory(categoryId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<unknown>>(() =>
      apiService.delete<TApiResponse<unknown>>(
        `${this.rootEndpoint}/chat/categories/${categoryId}`,
        undefined,
        options
      )
    );
  }

  // ── Sections (admin/sections/*) ──
  getSections(role?: "FUNDRAISER" | "INVESTOR", options?: TApiServiceOptions) {
    const endpoint = role
      ? `${this.rootEndpoint}/sections/by-role/${role}`
      : `${this.rootEndpoint}/sections/`;
    return handleAsync<TApiServiceError, TApiResponse<TAdminSection[]>>(() =>
      apiService.get<TApiResponse<TAdminSection[]>>(endpoint, options)
    );
  }

  createSection(payload: TCreateSectionRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminSection>>(() =>
      apiService.post<TApiResponse<TAdminSection>>(`${this.rootEndpoint}/sections/`, payload, options)
    );
  }

  updateSection(sectionId: string, payload: TUpdateSectionRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAdminSection>>(() =>
      apiService.put<TApiResponse<TAdminSection>>(
        `${this.rootEndpoint}/sections/${sectionId}`,
        payload,
        options
      )
    );
  }

  deleteSection(sectionId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ deleted: boolean }>>(() =>
      apiService.delete<TApiResponse<{ deleted: boolean }>>(
        `${this.rootEndpoint}/sections/${sectionId}`,
        undefined,
        options
      )
    );
  }

  // ── WhatsApp Templates (admin/whatsapp-templates/*) ──
  getWhatsAppTemplates(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TWhatsAppTemplate[]>>(() =>
      apiService.get<TApiResponse<TWhatsAppTemplate[]>>(`${this.rootEndpoint}/whatsapp-templates`, options)
    );
  }

  syncWhatsAppTemplates(status?: string, options?: TApiServiceOptions) {
    const endpoint = status
      ? `${this.rootEndpoint}/whatsapp-templates/sync?status=${encodeURIComponent(status)}`
      : `${this.rootEndpoint}/whatsapp-templates/sync`;
    return handleAsync<TApiServiceError, TApiResponse<{ upserted: number }>>(() =>
      apiService.post<TApiResponse<{ upserted: number }>>(endpoint, undefined, options)
    );
  }

  createWhatsAppTemplateFromLocal(templateName: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TWhatsAppTemplate>>(() =>
      apiService.post<TApiResponse<TWhatsAppTemplate>>(
        `${this.rootEndpoint}/whatsapp-templates/local/${encodeURIComponent(templateName)}`,
        undefined,
        options
      )
    );
  }

  updateWhatsAppTemplate(
    templateId: string,
    payload: TUpdateWhatsAppTemplateRequest,
    options?: TApiServiceOptions
  ) {
    return handleAsync<TApiServiceError, TApiResponse<TWhatsAppTemplate>>(() =>
      apiService.patch<TApiResponse<TWhatsAppTemplate>>(
        `${this.rootEndpoint}/whatsapp-templates/${templateId}`,
        payload,
        options
      )
    );
  }

  // ── WhatsApp Analytics (admin/whatsapp-analytics/*) ──
  getWhatsAppAnalyticsSummary(params?: TWhatsAppAnalyticsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.duration) queryParams.append("duration", params.duration);
    if (params?.company_limit !== undefined) queryParams.append("company_limit", String(params.company_limit));
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/whatsapp-analytics/summary?${queryString}`
      : `${this.rootEndpoint}/whatsapp-analytics/summary`;
    return handleAsync<TApiServiceError, TApiResponse<TWhatsAppAnalyticsSummary>>(() =>
      apiService.get<TApiResponse<TWhatsAppAnalyticsSummary>>(endpoint, options)
    );
  }

  // ── AI usage (admin/ai/*) ──
  getAiUsageStats(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAiUsageStats>>(() =>
      apiService.get<TApiResponse<TAiUsageStats>>(`${this.rootEndpoint}/ai/usage/stats`, options)
    );
  }

  getAiModelStats(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAiModelStats>>(() =>
      apiService.get<TApiResponse<TAiModelStats>>(`${this.rootEndpoint}/ai/models/stats`, options)
    );
  }

  getAiUsageLogs(params?: TAiUsageLogsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.company_id) queryParams.append("company_id", params.company_id);
    if (params?.model_name) queryParams.append("model_name", params.model_name);
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/ai/usage/logs?${queryString}`
      : `${this.rootEndpoint}/ai/usage/logs`;
    return handleAsync<TApiServiceError, TApiResponse<TPaginatedAiUsageLogs>>(() =>
      apiService.get<TApiResponse<TPaginatedAiUsageLogs>>(endpoint, options)
    );
  }

  getAiContexts(params?: { limit?: number; offset?: number }, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/ai/contexts?${queryString}`
      : `${this.rootEndpoint}/ai/contexts`;
    return handleAsync<TApiServiceError, TApiResponse<TAiContext[]>>(() =>
      apiService.get<TApiResponse<TAiContext[]>>(endpoint, options)
    );
  }

  // ── Files admin (admin/files/*) ──
  getFiles(params?: TFilesQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/files/?${queryString}`
      : `${this.rootEndpoint}/files/`;
    return handleAsync<TApiServiceError, TApiResponse<TPaginatedFiles>>(() =>
      apiService.get<TApiResponse<TPaginatedFiles>>(endpoint, options)
    );
  }

  deleteFile(fileId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ message: string }>>(() =>
      apiService.delete<TApiResponse<{ message: string }>>(
        `${this.rootEndpoint}/files/${fileId}`,
        undefined,
        options
      )
    );
  }

  getFileStatsOverview(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TFileStatsOverview>>(() =>
      apiService.get<TApiResponse<TFileStatsOverview>>(`${this.rootEndpoint}/files/stats/overview`, options)
    );
  }

  getFileStatsByType(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<Record<string, number>>>(() =>
      apiService.get<TApiResponse<Record<string, number>>>(`${this.rootEndpoint}/files/stats/by-type`, options)
    );
  }

  getFileStatsByUser(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<Record<string, number>>>(() =>
      apiService.get<TApiResponse<Record<string, number>>>(`${this.rootEndpoint}/files/stats/by-user`, options)
    );
  }

  // ── Deal Network / Investor Platform (admin/deal-network/*) ──
  getInvestorPlatformInvestors(
    params?: TInvestorPlatformInvestorsQueryParams,
    options?: TApiServiceOptions
  ) {
    const queryParams = new URLSearchParams();
    if (params?.investor_platform_id) queryParams.append("investor_platform_id", params.investor_platform_id);
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/deal-network/investors?${queryString}`
      : `${this.rootEndpoint}/deal-network/investors`;
    return handleAsync<TApiServiceError, TApiResponse<TPaginatedInvestorPlatformInvestors>>(() =>
      apiService.get<TApiResponse<TPaginatedInvestorPlatformInvestors>>(endpoint, options)
    );
  }

  getInvestorPlatformInvestor(investorId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TInvestorPlatformInvestor>>(() =>
      apiService.get<TApiResponse<TInvestorPlatformInvestor>>(
        `${this.rootEndpoint}/deal-network/investors/${investorId}`,
        options
      )
    );
  }

  createInvestorPlatformInvestor(
    payload: TCreateInvestorPlatformInvestorRequest,
    options?: TApiServiceOptions
  ) {
    return handleAsync<TApiServiceError, TApiResponse<TInvestorPlatformInvestor>>(() =>
      apiService.post<TApiResponse<TInvestorPlatformInvestor>>(
        `${this.rootEndpoint}/deal-network/investors`,
        payload,
        options
      )
    );
  }

  updateInvestorPlatformInvestor(
    investorId: string,
    payload: TUpdateInvestorPlatformInvestorRequest,
    options?: TApiServiceOptions
  ) {
    return handleAsync<TApiServiceError, TApiResponse<TInvestorPlatformInvestor>>(() =>
      apiService.put<TApiResponse<TInvestorPlatformInvestor>>(
        `${this.rootEndpoint}/deal-network/investors/${investorId}`,
        payload,
        options
      )
    );
  }

  deleteInvestorPlatformInvestor(investorId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ message: string }>>(() =>
      apiService.delete<TApiResponse<{ message: string }>>(
        `${this.rootEndpoint}/deal-network/investors/${investorId}`,
        undefined,
        options
      )
    );
  }

  getConnectRequests(params?: TConnectRequestsQueryParams, options?: TApiServiceOptions) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.rootEndpoint}/deal-network/connect-requests?${queryString}`
      : `${this.rootEndpoint}/deal-network/connect-requests`;
    return handleAsync<TApiServiceError, TApiResponse<TPaginatedConnectRequests>>(() =>
      apiService.get<TApiResponse<TPaginatedConnectRequests>>(endpoint, options)
    );
  }

  getConnectRequest(requestId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TConnectRequest>>(() =>
      apiService.get<TApiResponse<TConnectRequest>>(
        `${this.rootEndpoint}/deal-network/connect-requests/${requestId}`,
        options
      )
    );
  }

  updateConnectRequestStatus(
    requestId: string,
    payload: TUpdateConnectRequestStatusRequest,
    options?: TApiServiceOptions
  ) {
    return handleAsync<TApiServiceError, TApiResponse<TConnectRequest>>(() =>
      apiService.patch<TApiResponse<TConnectRequest>>(
        `${this.rootEndpoint}/deal-network/connect-requests/${requestId}`,
        payload,
        options
      )
    );
  }
}

export const adminService = new AdminService();
